const MAX_DIM = 1600;

async function imageBitmapToWebpFile(imageBitmap: ImageBitmap, sourceFileName: string, quality: number): Promise<File> {
  const { width, height } = imageBitmap;
  let newWidth = width;
  let newHeight = height;

  if (width > MAX_DIM || height > MAX_DIM) {
    const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
    newWidth = Math.round(width * ratio);
    newHeight = Math.round(height * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = newWidth;
  canvas.height = newHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context is not available.');

  ctx.drawImage(imageBitmap, 0, 0, newWidth, newHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('Failed to create WebP blob.'));
        resolve(new File([blob], sourceFileName.replace(/\.\w+$/, '.webp'), { type: 'image/webp' }));
      },
      'image/webp',
      quality
    );
  });
}

export function isHeicLikeFile(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif' || file.type === 'image/heic' || file.type === 'image/heif';
}

export async function convertHeicFilesToWebp(inputFiles: File | File[], quality: number = 0.9): Promise<File[]> {
  if (typeof window === 'undefined') {
    throw new Error('convertHeicFilesToWebp must run in the browser.');
  }

  const files: File[] = Array.isArray(inputFiles) ? inputFiles : [inputFiles];
  const { heicTo } = await import('heic-to');

  const convertOne = async (file: File): Promise<File> => {
    let imageBitmap: ImageBitmap | undefined;
    try {
      imageBitmap = await heicTo({ blob: file, type: 'bitmap' });
      return await imageBitmapToWebpFile(imageBitmap, file.name, quality);
    } finally {
      imageBitmap?.close();
    }
  };

  return Promise.all(files.map(convertOne));
}

export async function getConvertedToWebpImages(inputFiles: File | File[], quality: number = 0.9): Promise<File[]> {
  const files: File[] = Array.isArray(inputFiles) ? inputFiles : [inputFiles];

  const convertFile = async (file: File): Promise<File> => {
    let imageBitmap: ImageBitmap | undefined;
    try {
      imageBitmap = await createImageBitmap(file);
      return await imageBitmapToWebpFile(imageBitmap, file.name, quality);
    } finally {
      imageBitmap?.close();
    }
  };

  return Promise.all(files.map(convertFile));
}

export const convertImagesToWebpAndMergeWithAnotherFiles = async (files: File[]): Promise<File[]> => {
  const imageFiles = files.filter((file) => file.type.startsWith('image/'));
  const nonImageFiles = files.filter((file) => !file.type.startsWith('image/'));

  const convertedImages = imageFiles.length > 0 ? await getConvertedToWebpImages(imageFiles) : [];

  const filesToUpload: File[] = [];
  let imageIdx = 0;
  let nonImageIdx = 0;

  for (const file of files) {
    if (file.type.startsWith('image/')) {
      filesToUpload.push(convertedImages[imageIdx]);
      imageIdx++;
    } else {
      filesToUpload.push(nonImageFiles[nonImageIdx]);
      nonImageIdx++;
    }
  }

  return filesToUpload;
};
